import { ReactElement, Ref, useImperativeHandle, useState } from 'react'
import { ColorValue, LayoutChangeEvent, StyleSheet, View, ViewProps } from 'react-native'

import { Color, Radius, Spacing } from '../../style'

import { Text } from '../typography'

const AXIS_LABEL_HEIGHT = 20
const MAX_BAR_WIDTH = 26
const CONDENSED_BAR_WIDTH = 40
const MIN_LABEL_WIDTH = 16

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minHeight: 200,
  },
  content: {
    flex: 1,
    flexDirection: 'row',
    marginTop: 15,
  },
  verticalAxis: {
    flexDirection: 'row',
  },
  verticalTitle: {
    width: 300,
    marginLeft: -(300 - AXIS_LABEL_HEIGHT) / 2,
    marginRight: -(300 - AXIS_LABEL_HEIGHT) / 2 + Spacing.XS,
    alignItems: 'center',
    justifyContent: 'center',
  },
  verticalLabels: {
    height: '100%',
    marginRight: Spacing.XS,
  },
  verticalLabel: {
    lineHeight: 0,
    color: Color.metalGrey,
    textAlign: 'right',
  },
  data: {
    flex: 1,
    flexDirection: 'row',
    paddingHorizontal: 2,
  },
  column: {
    flex: 1,
    justifyContent: 'center',
  },
  value: {
    height: '100%',
    alignItems: 'flex-end',
    flexDirection: 'row',
  },
  barColumn: {
    flex: 1,
    alignItems: 'center',
    marginHorizontal: 1,
  },
  bar: {
    backgroundColor: Color.greyBlue,
    flex: 1,
    borderRadius: Radius.XS,
    width: '100%',
    maxWidth: MAX_BAR_WIDTH,
  },
  otherBar: {
    backgroundColor: Color.calmGrey,
    marginLeft: -10,
  },
  barLabel: {
    marginHorizontal: -10,
    marginTop: -10,
    textAlign: 'center',
    color: Color.premiumBlack,
  },
  horizontalAxis: {
    flexDirection: 'row',
  },
  horizontalAxisContent: {
    flex: 1,
  },
  horizontalAxisLabels: {
    flexDirection: 'row',
    paddingHorizontal: 2,
  },
  horizontalAxisTitle: {
    textAlign: 'center',
  },
  horizontalWithVerticalTitle: {
    marginLeft: AXIS_LABEL_HEIGHT + Spacing.XS,
  },
  horizontalLabel: {
    alignItems: 'center',
  },
  horizontalLabelRotated: {
    marginTop: Spacing.XS,
    width: 300,
    marginLeft: -(300 - AXIS_LABEL_HEIGHT) / 2,
    marginRight: -(300 - AXIS_LABEL_HEIGHT) / 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  axisLabel: {
    color: Color.metalGrey,
  },
  axisLabelRotated: {
    transform: [{ rotate: '270deg' }],
    marginTop: Spacing.XS,
  },
  verticalAxisTitle: {
    transform: [{ rotate: '270deg' }],
  },
  grid: {
    flex: 1,
    justifyContent: 'flex-end',
    ...StyleSheet.absoluteFillObject,
  },
  gridSpace: {
    width: '100%',
    justifyContent: 'flex-end',
  },
  gridLine: {
    marginTop: -0.5,
    height: 1,
    width: '100%',
    backgroundColor: Color.calmGrey,
  },
  hidden: {
    height: 0,
    opacity: 0,
  },
})

interface HorizontalAxis {
  title?: string | ReactElement
  renderLabel?: (index: number) => string | ReactElement
}

interface VerticalAxis {
  title?: string | ReactElement
  renderLabel?: (value: number) => string | ReactElement
  min?: number
  max?: number
  gridStep?: number
}

interface Data {
  values: (number | null)[]
  renderLabel?: (value: number, barIndex: number, valueIndex: number) => string
  backgroundColor?: ColorValue
}

interface Serie {
  bars: (number | null)[]
  index: number
}

export interface Props extends ViewProps {
  data: Data[]
  horizontalAxis?: HorizontalAxis
  verticalAxis?: VerticalAxis
  test_ref?: Ref<any>
}

const ChartBar = (props: Props) => {
  const { data, horizontalAxis, verticalAxis, test_ref, style, ...otherProps } = props
  const [barWidth, setBarWidth] = useState(CONDENSED_BAR_WIDTH)
  const barCondensed = barWidth < CONDENSED_BAR_WIDTH
  const { min = 0 } = verticalAxis || {}
  const max =
    verticalAxis?.max ||
    data.reduce(
      (acc, serie) =>
        Math.max(
          acc,
          serie.values.reduce<number>((serieAcc, value) => Math.max(serieAcc || 0, value || 0), 0),
        ),
      0,
    )
  const gridStep = verticalAxis?.gridStep || (max - min) / 5

  const series = data.reduce((acc, serie) => {
    let index = 0
    serie.values?.forEach((value, barIndex) => {
      acc[barIndex] = {
        index,
        bars: [...(acc[barIndex]?.bars || []), value] as (number | null)[],
      }
      if (acc[barIndex]?.bars.reduce((acc, barValue) => acc || barValue !== null, false) || value !== null) {
        index += 1
      } else {
        index = 0
      }
    })
    return acc
  }, [] as Serie[])

  useImperativeHandle(test_ref, () => ({
    layout: (event: LayoutChangeEvent) => {
      layout(event)
    },
  }))

  const layout = (event: LayoutChangeEvent) => {
    const width = event.nativeEvent.layout.width
    setBarWidth(width)
  }

  const renderVerticalTitle = () => {
    const title = verticalAxis?.title
    if (!title) return
    return (
      <View style={styles.verticalTitle}>
        {typeof title === 'string' ? (
          <Text style={styles.verticalAxisTitle} level={7} numberOfLines={1}>
            {title}
          </Text>
        ) : (
          title
        )}
      </View>
    )
  }

  const renderVerticalLabels = () => {
    const height = gridStep / (max - min)
    const numberOfLabels = Math.ceil(1 / height)

    const labels = new Array(numberOfLabels).fill('').map((_, index) => {
      const value = Math.round((min + gridStep * index) * 100) / 100
      const label = verticalAxis?.renderLabel?.(value)
      const space = index === numberOfLabels - 1 ? 1 - (numberOfLabels - 1) * height : height
      return (
        <View style={[styles.gridSpace, { height: `${space * 100}%` }]} key={`label-${index}`}>
          <Text level={7} style={styles.verticalLabel}>
            {label === undefined ? value : label}
          </Text>
        </View>
      )
    })
    const label = verticalAxis?.renderLabel?.(max)
    labels.push(
      typeof label === 'string' || label === undefined ? (
        <Text level={7} style={styles.verticalLabel} key="label-max">
          {label === undefined ? max : label}
        </Text>
      ) : (
        (label as ReactElement)
      ),
    )
    return <View style={styles.verticalLabels}>{labels.reverse()}</View>
  }

  const renderVerticalAxis = () => {
    return (
      <View style={styles.verticalAxis}>
        {renderVerticalTitle()}
        {renderVerticalLabels()}
      </View>
    )
  }

  const renderGrid = () => {
    const height = gridStep / (max - min)
    const numberOfLabels = Math.ceil(1 / height)

    const lines = new Array(numberOfLabels).fill('').map((_, index) => {
      const space = index === numberOfLabels - 1 ? 1 - (numberOfLabels - 1) * height : height
      return (
        <View style={[styles.gridSpace, { height: `${space * 100}%` }]} key={`line-${index}`}>
          <View style={styles.gridLine} />
        </View>
      )
    })
    lines.push(<View style={styles.gridLine} key="line-max" />)

    return <View style={styles.grid}>{lines.reverse()}</View>
  }

  const renderData = () => {
    return (
      <View style={styles.data}>
        {renderGrid()}
        {series.map((serie, serieIndex) => (
          <View style={[styles.column, styles.value]} key={`value-${serieIndex}`}>
            {serie.bars.map((value, barIndex) => {
              if (value === null) return null
              const label = data[barIndex]?.renderLabel?.(value, barIndex, serieIndex)
              const barStyle = data[barIndex]?.backgroundColor && { backgroundColor: data[barIndex].backgroundColor }
              return (
                <View
                  key={`bar-${barIndex}`}
                  style={[
                    styles.barColumn,
                    { height: `${((value - min) / (max - min)) * 100}%`, zIndex: serie.bars.length - barIndex },
                  ]}
                  onLayout={layout}
                >
                  <View style={[styles.bar, barIndex > 0 && styles.otherBar, barStyle]} testID="ChartBar.Bar">
                    {!barCondensed && (
                      <Text level={7} style={[styles.verticalLabel, styles.barLabel]}>
                        {label === undefined ? value : label}
                      </Text>
                    )}
                  </View>
                </View>
              )
            })}
          </View>
        ))}
      </View>
    )
  }

  const renderHorizontalAxis = () => {
    return (
      <View style={[styles.horizontalAxis, !!verticalAxis?.title && styles.horizontalWithVerticalTitle]}>
        <View style={styles.hidden}>{renderVerticalLabels()}</View>
        <View style={styles.horizontalAxisContent}>
          <View style={styles.horizontalAxisLabels}>
            {series.map((serie, index) => {
              const label = horizontalAxis?.renderLabel?.(index)
              const skip = Math.ceil(MIN_LABEL_WIDTH / barWidth)
              return (
                <View
                  pointerEvents="box-none"
                  style={[styles.column, styles.horizontalLabel, barCondensed && styles.horizontalLabelRotated]}
                  key={`label-${index}`}
                >
                  {(serie.index % skip !== 0) ? null : ((typeof label === 'string' || label === undefined) ? (
                    <Text
                      level={7}
                      style={[styles.axisLabel, barCondensed && styles.axisLabelRotated]}
                      numberOfLines={1}
                    >
                      {label === undefined ? index : label}
                    </Text>
                  ) : (
                    label
                  ))}
                </View>
              )
            })}
          </View>
          {horizontalAxis?.title &&
            (typeof horizontalAxis.title === 'string' ? (
              <Text style={styles.horizontalAxisTitle} level={7} numberOfLines={1}>
                {horizontalAxis?.title}
              </Text>
            ) : (
              horizontalAxis?.title
            ))}
        </View>
      </View>
    )
  }

  return (
    <View style={[styles.container, style]} {...otherProps}>
      <View style={styles.content}>
        {renderVerticalAxis()}
        {renderData()}
      </View>
      {renderHorizontalAxis()}
    </View>
  )
}

export default ChartBar
